import { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";

const API = "https://erp-xe9u.onrender.com/api";
const DOC_TYPES = ["sales_order", "vendor_email", "site_email"];

const isPdf = (a) =>
  DOC_TYPES.includes(a.type) ||
  (a.contentType || "") === "application/pdf" ||
  /\.pdf$/i.test(a.name || "") ||
  /\.pdf$/i.test(a.url || "");

const isImage = (a) => {
  if (!a.url && !a.fileId) return false;
  if (isPdf(a)) return false;
  if (a.fileId)
    return (
      (a.contentType || "").startsWith("image/") || a.type === "packing_slip"
    );
  return (
    a.type === "packing_slip" ||
    a.type === "site_image" ||
    /\.(jpg|jpeg|png|gif|webp)$/i.test(a.url) ||
    (a.url || "").includes("cloudinary")
  );
};

const fetchBlob = async (fileId) => {
  const token = localStorage.getItem("token");
  const res = await axios.get(`${API}/materialOrders3/files/${fileId}`, {
    headers: { Authorization: `Bearer ${token}` },
    responseType: "blob",
  });
  return res.data;
};

// Returns a displayable src for an attachment (object URL for GridFS files)
function useAttSrc(att) {
  const [src, setSrc] = useState(att.fileId ? null : att.url || null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!att.fileId) {
      setSrc(att.url || null);
      return;
    }
    let url = null;
    let cancelled = false;
    setSrc(null);
    setFailed(false);
    fetchBlob(att.fileId)
      .then((blob) => {
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setSrc(url);
      })
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [att.fileId, att.url]);

  return { src, failed };
}

function Thumb({ att, onClick }) {
  const { src, failed } = useAttSrc(att);
  return (
    <button
      onClick={onClick}
      className="relative aspect-square rounded-lg overflow-hidden border border-gray-600 bg-gray-700 hover:border-orange-500 transition-colors"
      title={att.name || "Photo"}
    >
      {src ? (
        <img
          src={src}
          alt={att.name || "Photo"}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      ) : (
        <span className="absolute inset-0 flex items-center justify-center text-[10px] text-gray-500">
          {failed ? "Failed to load" : "Loading…"}
        </span>
      )}
    </button>
  );
}

function Lightbox({ images, index, onClose, onIndex }) {
  const att = images[index];
  const { src, failed } = useAttSrc(att);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && index > 0) onIndex(index - 1);
      if (e.key === "ArrowRight" && index < images.length - 1)
        onIndex(index + 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, images.length, onClose, onIndex]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-5 text-3xl text-white/80 hover:text-white"
      >
        ×
      </button>
      <div className="absolute top-4 left-1/2 -translate-x-1/2 text-xs text-white/70 bg-black/50 px-3 py-1 rounded-full">
        {index + 1} / {images.length}
      </div>
      {index > 0 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onIndex(index - 1);
          }}
          className="absolute left-4 text-4xl text-white/70 hover:text-white px-3"
        >
          ‹
        </button>
      )}
      {index < images.length - 1 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onIndex(index + 1);
          }}
          className="absolute right-4 text-4xl text-white/70 hover:text-white px-3"
        >
          ›
        </button>
      )}
      <div
        className="max-w-[90vw] max-h-[85vh] flex flex-col items-center"
        onClick={(e) => e.stopPropagation()}
      >
        {src ? (
          <img
            src={src}
            alt={att.name || "Photo"}
            className="max-w-[90vw] max-h-[80vh] object-contain"
          />
        ) : (
          <span className="text-gray-400 text-sm">
            {failed ? "Failed to load image" : "Loading…"}
          </span>
        )}
        {att.name && (
          <span className="mt-2 text-xs text-white/60 truncate max-w-[80vw]">
            {att.name}
          </span>
        )}
      </div>
    </div>
  );
}

const DOC_LABEL = {
  sales_order: "Sales Order",
  vendor_email: "Vendor Copy",
  site_email: "Site Copy",
  packing_slip: "Packing Slip",
};

export default function AttachmentsPanel3({ order }) {
  const attachments = order?.attachments || [];
  const [lightbox, setLightbox] = useState(null);
  const [opening, setOpening] = useState(null);

  const images = useMemo(() => attachments.filter(isImage), [attachments]);
  const docs = useMemo(
    () => attachments.filter((a) => (a.url || a.fileId) && !isImage(a)),
    [attachments],
  );

  const openDoc = async (att) => {
    // open the tab synchronously so popup blockers allow it
    const win = window.open("", "_blank");
    try {
      setOpening(att._id || att.fileId || att.url);
      if (att.fileId) {
        const blob = await fetchBlob(att.fileId);
        const url = URL.createObjectURL(
          new Blob([blob], { type: "application/pdf" }),
        );
        if (win) win.location.href = url;
        else window.location.href = url;
      } else if (att.url) {
        // legacy Cloudinary: pull as blob so the browser previews it inline
        try {
          const res = await axios.get(att.url, { responseType: "blob" });
          const url = URL.createObjectURL(
            new Blob([res.data], { type: "application/pdf" }),
          );
          if (win) win.location.href = url;
          else window.location.href = url;
        } catch {
          if (win) win.location.href = att.url;
        }
      }
    } catch (err) {
      if (win) win.close();
      alert("Could not open document");
    } finally {
      setOpening(null);
    }
  };

  if (!images.length && !docs.length) return null;

  return (
    <div className="space-y-4">
      {images.length > 0 && (
        <div>
          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">
            Photos ({images.length})
          </p>
          <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8 gap-2">
            {images.map((att, i) => (
              <Thumb
                key={String(att._id || att.fileId || att.url || i)}
                att={att}
                onClick={() => setLightbox(i)}
              />
            ))}
          </div>
        </div>
      )}

      {docs.length > 0 && (
        <div>
          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">
            Documents ({docs.length})
          </p>
          <div className="space-y-1.5">
            {docs.map((att, i) => (
              <button
                key={String(att._id || att.fileId || att.url || i)}
                onClick={() => openDoc(att)}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg bg-gray-700/40 border border-gray-700 hover:border-orange-500/60 text-left transition-colors"
              >
                <span className="text-blue-400 text-sm">📄</span>
                <span className="flex-1 truncate text-xs text-gray-200">
                  {att.name || `Document ${i + 1}`}
                </span>
                <span className="px-2 py-0.5 text-[10px] font-semibold rounded bg-blue-900/40 text-blue-300">
                  {opening === (att._id || att.fileId || att.url)
                    ? "Opening…"
                    : DOC_LABEL[att.type] || "Document"}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {lightbox !== null && images[lightbox] && (
        <Lightbox
          images={images}
          index={lightbox}
          onClose={() => setLightbox(null)}
          onIndex={setLightbox}
        />
      )}
    </div>
  );
}
